// @owners { team: patients-team }
import { type AltoIconName, COLORS } from '@alto/design-library-tokens';
import { Body, Button, Description, InlineAlert, LandingPage, LgPadding, XsSpacing } from '@alto/design-system';
import React from 'react';
import { getImage } from '~shared/assets/images/getImage';
import { getCompanyName } from '~shared/config/getCompanyName';
import { DEFAULT_ERROR_ALERT_MESSAGE, IMG_ALT_TEXTS } from '~shared/constants';
import { useIsFeatureDisabledForCompany } from '~shared/hooks';
import { useLoginViewed } from '~shared/lib/analytics/src/hooks/useLoginViewed';

type Props = {
  readonly login: () => void;
  readonly loading: boolean;
  readonly error: boolean;
  readonly errorMessage?: string;
  readonly disableLogin?: boolean;
  readonly navigateToOnboarding: () => void;
  readonly iconRight?: AltoIconName | undefined;
  readonly disableOnboarding?: boolean;
};
export const WelcomePage = ({
  login,
  loading,
  error,
  errorMessage,
  disableLogin = false,
  navigateToOnboarding,
  iconRight,
  disableOnboarding = false,
}: Props) => {
  useLoginViewed({ inAuth0Flow: true });
  const errorText = errorMessage ?? DEFAULT_ERROR_ALERT_MESSAGE;
  const { isFeatureDisabled: shouldDisableCreateAccount, isLoading: isFeatureFlagLoading } =
    useIsFeatureDisabledForCompany('create_account');

  return (
    <LandingPage
      title={`Welcome to ${getCompanyName()}!`}
      HeaderBodyCopy={<Body>We’re your trusted pharmacy offering prescription delivery and extraordinary care.</Body>}
      backgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
      illustrationSrc={getImage('deliveryCourierWithPatient')}
      illustrationAccessibilityLabel={IMG_ALT_TEXTS.deliveryCourierWithPatient}
    >
      {error ? (
        <LgPadding>
          <InlineAlert type="error">
            <Description center>{errorText}</Description>
          </InlineAlert>
        </LgPadding>
      ) : null}
      <Button
        label="Log in"
        onPress={login}
        loading={loading}
        alignment="center"
        testID="login-button"
        iconRightName={iconRight}
        disabled={disableLogin}
      />
      {!shouldDisableCreateAccount && (
        <>
          <XsSpacing />
          <Button
            type="tertiary"
            label="Create an account"
            onPress={navigateToOnboarding}
            alignment="center"
            testID="create-account"
            disabled={disableOnboarding || loading || isFeatureFlagLoading}
          />
        </>
      )}
    </LandingPage>
  );
};
